import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { ApiError, describeApiError } from "@/api/client";
import {
  type ClientRole,
  type CurrentUser,
  type ProvisionProfileInput,
  type UpdateCurrentUserPreferencesInput,
} from "@/api/schemas";
import { washboxApi } from "@/api/washbox";
import { keycloakSession } from "@/auth/keycloak-session";

export type IdentityStatus = "idle" | "loading" | "unprovisioned" | "ready" | "error";

/**
 * The single "profile type" that drives which pages and dashboard sections a
 * user sees. Every account carries the CUSTOMER baseline role, so a user is a
 * cleaner or a dry cleaner when the higher role is present.
 */
export type ProfileType = "owner" | "cleaner" | "dry-cleaner";

export const useIdentityStore = defineStore("identity", () => {
  const status = ref<IdentityStatus>("idle");
  const currentUser = ref<CurrentUser | null>(null);
  const errorMessage = ref<string | null>(null);
  let loading: Promise<void> | null = null;
  let epoch = 0;

  const needsProfile = computed(() => status.value === "unprovisioned");
  const roles = computed<readonly ClientRole[]>(() => currentUser.value?.roles ?? []);
  const isCustomer = computed(() => roles.value.includes("CUSTOMER"));
  const isCleaner = computed(() => roles.value.includes("CLEANER"));
  const isLaundryProvider = computed(
    () => roles.value.includes("LINEN_PROVIDER") && roles.value.includes("ORG_ADMIN"),
  );
  const profileType = computed<ProfileType>(() => {
    if (isLaundryProvider.value) return "dry-cleaner";
    if (isCleaner.value) return "cleaner";
    return "owner";
  });

  keycloakSession.subscribe((authenticated) => {
    if (!authenticated) {
      reset();
    }
  });

  function load(force = false): Promise<void> {
    if (!force && (status.value === "ready" || status.value === "unprovisioned")) {
      return Promise.resolve();
    }
    if (loading !== null) {
      return loading;
    }

    status.value = "loading";
    errorMessage.value = null;
    const loadEpoch = epoch;
    const loadPromise = washboxApi
      .getCurrentUser()
      .then((user) => {
        if (epoch !== loadEpoch) {
          return;
        }
        currentUser.value = user;
        status.value = "ready";
      })
      .catch((error: unknown) => {
        if (epoch !== loadEpoch) {
          return;
        }
        if (error instanceof ApiError && error.code === "USER_NOT_PROVISIONED") {
          currentUser.value = null;
          status.value = "unprovisioned";
          return;
        }
        status.value = "error";
        errorMessage.value = describeApiError(error);
      })
      .finally(() => {
        if (loading === loadPromise) {
          loading = null;
        }
      });
    loading = loadPromise;
    return loadPromise;
  }

  async function provision(input: ProvisionProfileInput): Promise<void> {
    const provisionEpoch = epoch;
    status.value = "loading";
    errorMessage.value = null;
    try {
      const user = await washboxApi.provisionCurrentUser(input);
      if (epoch !== provisionEpoch) {
        return;
      }
      currentUser.value = user;
      status.value = "ready";
    } catch (error) {
      if (epoch !== provisionEpoch) {
        return;
      }
      status.value = "unprovisioned";
      errorMessage.value = describeApiError(error);
      throw error;
    }
  }

  async function updatePreferences(input: UpdateCurrentUserPreferencesInput): Promise<void> {
    const updateEpoch = epoch;
    const user = await washboxApi.updateCurrentUserPreferences(input);
    if (epoch !== updateEpoch) return;
    currentUser.value = user;
    status.value = "ready";
    errorMessage.value = null;
  }

  function reset(): void {
    epoch += 1;
    status.value = "idle";
    currentUser.value = null;
    errorMessage.value = null;
    loading = null;
  }

  return {
    status,
    currentUser,
    errorMessage,
    needsProfile,
    roles,
    isCustomer,
    isCleaner,
    isLaundryProvider,
    profileType,
    load,
    provision,
    updatePreferences,
    reset,
  };
});
